import { useCallback, useEffect, useState } from 'react'

export interface Resource<T> {
  data: T | null
  error: unknown
  loading: boolean
  /** Refaz a busca. Usado depois de toda escrita — a lista vem do servidor. */
  reload: () => void
}

/**
 * Uma busca, seu erro e seu estado de carga.
 *
 * `fetcher` precisa ser estável (`useCallback`): é ele, e não um array de
 * dependências, que decide quando refazer a chamada — assim o filtro que mudou
 * fica declarado no `useCallback` de quem chama, onde dá para ler.
 *
 * O dado anterior **permanece** enquanto a próxima página carrega. Esvaziar a
 * tabela a cada clique de paginação faz a página saltar e pisca a altura toda.
 *
 * Não há cache: depois de uma escrita a tela precisa do estado do servidor, não
 * de uma cópia que a interface acha que está certa.
 */
export function useResource<T>(fetcher: (signal: AbortSignal) => Promise<T>): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    setLoading(true)

    fetcher(controller.signal)
      .then((value) => {
        if (cancelled) return
        setData(value)
        setError(null)
        setLoading(false)
      })
      .catch((cause: unknown) => {
        // Abortar é o comportamento normal do cleanup, não uma falha de tela.
        if (cancelled || controller.signal.aborted) return
        setError(cause)
        setLoading(false)
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [fetcher, nonce])

  const reload = useCallback(() => setNonce((value) => value + 1), [])

  return { data, error, loading, reload }
}
